import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Building2, ArrowLeft, Briefcase, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import PropertyCard from "@/components/PropertyCard";
import { VerifiedBadge } from "@/components/VerifiedBadges";
import { FLAGS } from "@/lib/utils";

export default function UserProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/users/${id}/public`);
        setProfile(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "error");
      }
    })();
  }, [id]);

  if (error === "not_found") return <Navigate to="/home" replace />;
  if (!profile) return <div className="max-w-4xl mx-auto text-center py-20 text-sm text-neutral-500">Loading profile…</div>;

  const isDeveloper = profile.role === "developer";
  const joined = profile.created_at ? new Date(profile.created_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : null;

  return (
    <div className="max-w-4xl mx-auto">
      <Link to={-1} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 mb-4" data-testid="profile-back">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="soft-card p-6 md:p-8">
        <div className="flex items-start gap-5 flex-wrap">
          <Avatar name={profile.name} src={profile.avatar} size={88} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-neutral-900" data-testid="profile-name">{profile.name}</h1>
              {profile.verified && <VerifiedBadge kind="developer" testId="profile-verified-developer-badge" />}
            </div>
            <p className="text-sm text-neutral-500 mt-1 capitalize">{isDeveloper ? "Property Developer" : "Investor"}</p>
            <div className="flex items-center gap-4 mt-3 flex-wrap text-xs text-neutral-500">
              {profile.country && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-base">{FLAGS[profile.country] || ""}</span>
                  <MapPin size={12} /> {profile.country}
                </span>
              )}
              {isDeveloper && profile.company_name && (
                <span className="inline-flex items-center gap-1.5">
                  <Briefcase size={12} /> {profile.company_name}
                </span>
              )}
              {isDeveloper && profile.years_experience && (
                <span>{profile.years_experience} yrs experience</span>
              )}
              {joined && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} /> Joined {joined}
                </span>
              )}
            </div>

            {profile.bio && (
              <p className="mt-5 text-neutral-700 leading-relaxed">{profile.bio}</p>
            )}
          </div>
        </div>

        {isDeveloper && profile.company_registration && (
          <p className="text-xs text-neutral-500 mt-6 pt-6 border-t border-neutral-100">
            <span className="font-semibold text-neutral-700">Company registration:</span> {profile.company_registration}
          </p>
        )}
      </div>

      {isDeveloper && (
        <div className="mt-10">
          <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2"><Building2 size={18} /> Developments by {profile.name?.split(" ")[0]}</h2>
          {(profile.developments || []).length === 0 ? (
            <p className="text-sm text-neutral-500 mt-4">No developments listed yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
              {profile.developments.map((d) => (
                <PropertyCard key={d.id} dev={d} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
